import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';
import { ResizeNotifier } from '@theme/utilities';

/**
 * Fired on `document` by anything that adds to, removes from or resizes the cart
 * drawer summary, so the sticky measurement below can be redone immediately.
 * The theme's transmitter guard (assets/se-brand.js) dispatches it when it shows
 * or hides the missing-transmitter warning.
 */
const CART_SUMMARY_CHANGED = 'cart-summary:changed';

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 * This component handles the remaining cart-specific concerns: auto-open on add-to-cart,
 * sticky summary layout, and the installments CTA close-on-click.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {

  /**
   * Re-measures whenever the summary's own height changes.
   *
   * `#updateStickyState` otherwise only runs on drawer-open and after a cart
   * lines update, so anything that changes the summary's height afterwards left
   * the sticky decision stale. When the summary grows past the threshold while
   * still marked sticky, it is a `position: sticky; bottom: 0` box taller than
   * its own scrollport: `.cart-drawer__items` is squeezed to zero height and the
   * line items — and eventually the checkout button — can no longer be scrolled
   * to. Watching the element itself covers every cause, whether it is theme
   * content (the transmitter warning, the pickup-preference control) or an app
   * block we do not control.
   *
   * @type {ResizeNotifier}
   */
  #summaryResizeObserver = new ResizeNotifier(() => this.#updateStickyState());

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);

    // The restore path sets [open] before this module loads, so the
    // theme-drawer:open event will have already fired. Use the attribute
    // check so this works even before <theme-drawer> upgrades.
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }

    document.addEventListener(CART_SUMMARY_CHANGED, this.#handleSummaryChanged);
    this.#observeSummary();
    // The server renders the safe, non-sticky layout; measure once we are live.
    this.#updateStickyState();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    document.removeEventListener(CART_SUMMARY_CHANGED, this.#handleSummaryChanged);
    this.#summaryResizeObserver.disconnect();
  }

  /**
   * Re-measures when something announces that it has changed the summary's
   * contents. ResizeObserver already covers this, but it delivers at a frame
   * boundary; anything that mutates the summary and knows it can say so and get
   * a synchronous re-measure instead of waiting a frame.
   */
  #handleSummaryChanged = () => {
    this.#observeSummary();
    this.#updateStickyState();
  };

  /**
   * Watches the summary so a height change re-runs the sticky measurement.
   * Re-called after cart re-renders, which replace the summary element.
   */
  #observeSummary() {
    this.#summaryResizeObserver.disconnect();
    const summary = this.#dialog?.querySelector('.cart-drawer__summary');
    if (summary) this.#summaryResizeObserver.observe(summary);
  }

  /**
   * Handles the theme-drawer opening — updates sticky state and wires up the installments CTA.
   */
  #handleDrawerOpen = () => {
    this.#observeSummary();
    this.#updateStickyState();

    // Close cart drawer when installments CTA is clicked to avoid overlapping dialogs.
    // Re-queried on every open so it survives cart content re-renders that
    // replace the shopify-payment-terms shadow root.
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.#themeDrawer?.close(), { once: true });
    });
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen = this.hasAttribute('auto-open') && event.action === 'add' && !this.#themeDrawer?.isOpen;

    // When the event originates inside an open MODAL <dialog> (e.g. quick-add),
    // defer the auto-open until that dialog's native `close` fires so its focus
    // restoration runs first — otherwise we'd capture the wrong
    // `#previouslyFocused`. Non-modal dialogs (e.g. the hotspot preview) don't
    // close on add and don't move focus, so `:modal` excludes them.
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    if (shouldAutoOpen && !sourceModal && !this.#isCartEmpty()) {
      this.#themeDrawer?.open();
    }

    event.promise
      ?.then(({ detail }) => {
        const settle = () =>
          requestAnimationFrame(() => {
            // The summary element is replaced by the cart re-render.
            this.#observeSummary();
            this.#updateStickyState();
          });

        if (!shouldAutoOpen || detail?.didError) {
          settle();
          return;
        }

        const openAndSettle = () => {
          if (!this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
          settle();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', openAndSettle, { once: true });
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      });
  };

  #isCartEmpty() {
    return Boolean(this.querySelector('.cart-drawer--empty'));
  }

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    // SE: the summary is permanently non-sticky.
    //
    // The height-ratio upgrade used to flip this back to "true" whenever the
    // summary looked small enough. That measurement is unreliable by nature:
    // PayPal and other accelerated buttons render asynchronously and grow the
    // summary AFTER it has been taken, and a sticky summary taller than its own
    // scrollport squeezes .cart-drawer__items to zero and clips the bottom of the
    // express buttons — the customer could see a sliver of the PayPal button and
    // could not scroll to it.
    //
    // Reliability beats pinned totals: one natural scroll container for the whole
    // drawer, always. `summary` and `content` are still resolved above so the
    // empty-cart guard and the observer wiring stay exactly as they were.
    void content;
    void summary;
    dialog.setAttribute('cart-summary-sticky', 'false');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
